// Pestaña «Códigos y cupones» con el mismo lista + detalle que «Promos venta»: arriba se elige entre
// códigos de descuento y cupones emitidos; el detalle es de solo lectura, o el alta de un código.
import { useMemo, useState } from 'react';
import { TicketPercent } from 'lucide-react';
import { toast } from 'sonner';
import { Button, ConfirmDialog, ErrorState, usePermissions } from '@/kit';
import { ChoiceChips, FieldHelp } from '@/shared/patterns/EditorForm';
import { LifecycleMasterDetail } from '@/shared/patterns/Lifecycle';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import {
    useDiscountCodes,
    useDiscountCodesByRetailer,
    useVoidDiscountCode,
    type ChainDiscountCode,
} from '../api/useDiscountCodes';
import { usePromotionFormSources, type PromotionFormSources } from '../api/usePromotionFormSources';
import { ALL_RETAILERS } from '../constants/retailerFilter';
import {
    ALL_CODE_STATUSES,
    CODE_VIEW_COPY,
    CODE_VIEW_OPTIONS,
    DEFAULT_CODE_SORT,
    DEFAULT_CODE_VIEW,
} from '../constants/discountCodes';
import { DISCOUNT_CODE_HELP } from '../constants/helpText';
import {
    matchesCodeSearch,
    sortCodes,
    toCodeFilters,
    type CodeListSort,
    type CodeView,
} from '../lib/discountCodesList';
import { voidConfirmCopy, voidErrorDescription } from '../lib/discountCodeVoidCopy';
import { PROMOTIONS_PERMISSIONS } from '../permissions';
import { DiscountCodeCreatePanel } from './DiscountCodeCreatePanel';
import { DiscountCodeDetail } from './DiscountCodeDetail';
import { DiscountCodesListPanel } from './DiscountCodesListPanel';

type Selection =
    | { kind: 'none' }
    | { kind: 'existing'; codeId: string }
    | { kind: 'new'; draftKey: number };

const NONE: Selection = { kind: 'none' };

/** Las cadenas del admin de tenant: los códigos se leen por cadena y se juntan. */
export interface DiscountCodesRetailers {
    ids: readonly string[];
    loading: boolean;
    names: ReadonlyMap<string, string>;
    options: readonly { value: string; label: string }[];
    error: unknown;
    refetch: () => Promise<unknown>;
}

export interface DiscountCodesPanelProps {
    sources: PromotionFormSources;
    /** Día CDMX `YYYY-MM-DD` contra el que se pintan los calendarios. */
    today: string;
    retailers?: DiscountCodesRetailers;
}

export function DiscountCodesPanel({ sources, today, retailers }: DiscountCodesPanelProps) {
    const { hasPermission } = usePermissions();
    const canCreate = hasPermission(PROMOTIONS_PERMISSIONS.codeCreate);
    const multi = retailers !== undefined;
    const [view, setView] = useState<CodeView>(DEFAULT_CODE_VIEW);
    const [status, setStatus] = useState(ALL_CODE_STATUSES);
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState<CodeListSort>(DEFAULT_CODE_SORT);
    const [retailerFilter, setRetailerFilter] = useState(ALL_RETAILERS);
    const [selection, setSelection] = useState<Selection>(NONE);
    const [newRetailerId, setNewRetailerId] = useState('');
    const [created, setCreated] = useState<ChainDiscountCode | null>(null);
    const [voiding, setVoiding] = useState<ChainDiscountCode | null>(null);
    const filters = toCodeFilters(view, status);
    const single = useDiscountCodes({ filters, enabled: !multi });
    const byRetailer = useDiscountCodesByRetailer({
        filters,
        // Con una cadena filtrada solo se lee y se pagina esa.
        retailerIds: retailerFilter === ALL_RETAILERS ? (retailers?.ids ?? []) : [retailerFilter],
        enabled: multi,
        retailersLoading: retailers?.loading ?? false,
    });
    const codes = multi ? byRetailer : single;
    // Sin la lista de cadenas no hay códigos que leer: es una falla con reintento.
    const list = retailers?.error
        ? { ...codes, isSuccess: false, error: retailers.error, refetch: retailers.refetch }
        : codes;
    const voidCode = useVoidDiscountCode();
    const copy = CODE_VIEW_COPY[view];
    const creatable = canCreate && view === 'ADMIN';

    const items = useMemo(
        () =>
            sortCodes(
                list.items.filter(
                    (code) =>
                        (retailerFilter === ALL_RETAILERS || code.retailerId === retailerFilter) &&
                        matchesCodeSearch(code, search),
                ),
                sort,
            ),
        [list.items, retailerFilter, search, sort],
    );
    const selectedId = selection.kind === 'existing' ? selection.codeId : null;
    // Recién creado puede no venir todavía en la lista: se muestra el que devolvió el alta.
    const selected =
        selectedId === null
            ? null
            : (list.items.find((code) => code.codeId === selectedId) ??
              (created?.codeId === selectedId ? created : null));
    const activeRetailerId = selection.kind === 'new' ? newRetailerId : selected?.retailerId;
    const chainSources = usePromotionFormSources(multi, activeRetailerId || undefined);
    const panelSources = multi ? chainSources : sources;

    const startNew = () => {
        setNewRetailerId(retailers?.ids.length === 1 ? retailers.ids[0] : '');
        setSelection({ kind: 'new', draftKey: Date.now() });
    };

    const confirmVoid = (code: ChainDiscountCode) => {
        const isCoupon = code.origin === 'SALE_COUPON';
        voidCode.mutate(
            {
                codeId: code.codeId,
                ...(multi && code.retailerId ? { retailerId: code.retailerId } : {}),
            },
            {
                onSuccess: () => {
                    setVoiding(null);
                    toast.success(isCoupon ? 'Cupón anulado' : 'Código anulado', {
                        description: code.codeText,
                    });
                },
                onError: (error) => {
                    setVoiding(null);
                    toast.error(`No se pudo anular el ${isCoupon ? 'cupón' : 'código'}`, {
                        description: voidErrorDescription(error),
                    });
                },
            },
        );
    };

    const changeView = (next: CodeView) => {
        setView(next);
        setStatus(ALL_CODE_STATUSES);
        setSelection(NONE);
    };

    return (
        <>
            <LifecycleMasterDetail
                list={
                    <>
                        <div className="flex flex-none items-center gap-1 border-b border-border px-3 py-2">
                            <ChoiceChips
                                label="Qué ver"
                                options={CODE_VIEW_OPTIONS}
                                value={view}
                                onPick={changeView}
                                disabled={selection.kind === 'new'}
                            />
                            <FieldHelp label="Qué ver" help={DISCOUNT_CODE_HELP.view} />
                        </div>
                        <DiscountCodesListPanel
                            view={view}
                            items={items}
                            loaded={list.isSuccess || Boolean(list.error)}
                            loadedCount={list.items.length}
                            hasMore={list.hasNextPage}
                            today={today}
                            search={search}
                            onSearchChange={setSearch}
                            status={status}
                            onStatusChange={setStatus}
                            sort={sort}
                            onSortChange={setSort}
                            selectedId={selectedId}
                            onSelect={(code) => {
                                setSelection({ kind: 'existing', codeId: code.codeId });
                            }}
                            onCreate={creatable ? startNew : null}
                            error={
                                list.error
                                    ? {
                                          title: copy.loadError,
                                          body:
                                              list.error instanceof Error
                                                  ? list.error.message
                                                  : undefined,
                                          onRetry: () => {
                                              void list.refetch();
                                          },
                                      }
                                    : null
                            }
                            footer={renderListFooter()}
                            {...(retailers
                                ? {
                                      retailers: {
                                          names: retailers.names,
                                          value: retailerFilter,
                                          onChange: setRetailerFilter,
                                      },
                                  }
                                : {})}
                        />
                    </>
                }
                detail={renderDetail()}
            />
            {voiding ? (
                <ConfirmDialog
                    open
                    onOpenChange={(open) => {
                        if (!open) setVoiding(null);
                    }}
                    {...voidConfirmCopy(voiding)}
                    confirmLabel="Anular"
                    destructive
                    loading={voidCode.isPending}
                    onConfirm={() => {
                        confirmVoid(voiding);
                    }}
                />
            ) : null}
        </>
    );

    function renderListFooter() {
        if (list.error) return null;
        if (list.loadMoreError) {
            return (
                <div className="p-2">
                    <ErrorState
                        title={`No pudimos cargar más ${copy.many}`}
                        description={
                            list.loadMoreError instanceof Error
                                ? list.loadMoreError.message
                                : undefined
                        }
                        onRetry={() => {
                            void list.fetchNextPage();
                        }}
                    />
                </div>
            );
        }
        if (!list.hasNextPage) return null;
        return (
            <div className="flex flex-none justify-center border-t border-border p-2">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                        void list.fetchNextPage();
                    }}
                    isLoading={list.isFetchingNextPage}
                >
                    Cargar más
                </Button>
            </div>
        );
    }

    function renderDetail() {
        if (selection.kind === 'new' && creatable) {
            return (
                <DiscountCodeCreatePanel
                    key={`new#${String(selection.draftKey)}`}
                    sources={panelSources}
                    today={today}
                    {...(retailers
                        ? {
                              retailer: {
                                  options: retailers.options,
                                  value: newRetailerId,
                                  onChange: setNewRetailerId,
                              },
                          }
                        : {})}
                    onCreated={(code) => {
                        setCreated(code);
                        setSelection({ kind: 'existing', codeId: code.codeId });
                    }}
                    onCancel={() => {
                        setSelection(NONE);
                    }}
                />
            );
        }
        if (selected) {
            return (
                <DiscountCodeDetail
                    key={selected.codeId}
                    code={selected}
                    sources={panelSources}
                    today={today}
                    onVoid={setVoiding}
                />
            );
        }
        return (
            <MasterDetailEmpty
                icon={<TicketPercent className="size-10" aria-hidden />}
                title={view === 'ADMIN' ? 'Selecciona un código' : 'Selecciona un cupón'}
                sub={
                    creatable ? (
                        <>
                            Elige una fila de la izquierda para verlo,
                            <br />o crea un código de descuento nuevo.
                        </>
                    ) : (
                        'Elige una fila de la izquierda para verlo.'
                    )
                }
                action={
                    creatable ? (
                        <Button size="sm" onClick={startNew}>
                            Nuevo código
                        </Button>
                    ) : null
                }
            />
        );
    }
}
